<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import { IconAdd, IconCheckCircle, IconEye } from '@/components/design/icons';
import GaiaButton from '@/components/design/base/GaiaButton.vue';
import GaiaDivider from '@/components/design/base/GaiaDivider.vue';
import GaiaTooltip from '@/components/design/base/GaiaTooltip.vue';
import {
  TemplateFieldType,
  type AnnotationFilter,
  type AnnotationInstance,
  type FieldCatalogEntity,
  type FieldCatalogField,
  type TemplateAnnotation,
} from '@/api';
import { useOfficeTemplateStore } from '@/office/stores/officeTemplateStore';
import { TemplateAuthoringService } from '@/services/contractGeneration/templateAuthoringService';
import {
  exportCurrentDocumentAsDocx,
  insertTextAtSelection,
  readDocumentBodyText,
  replaceAllTagOccurrences,
  wrapSelectionWithBlock,
} from '@/office/word/templateWord';
import {
  blockTagsReferenceRoot,
  buildDraftForDetectedField,
  draftVariableRoot,
  extractFieldTagOccurrences,
  resolveFieldAtPath,
} from '@/office/word/templateFieldEditing';
import {
  renderJinjaPreview,
  type AnnotationDraft,
} from '@/composables/contractGeneration/useTemplateAnnotations';
import {
  newConditionalSnippet,
  newLoopSnippet,
  renderControlFlowSnippet,
  renderControlFlowWrapParts,
  type ControlFlowSnippet,
} from '@/composables/contractGeneration/useControlFlowSnippets';
import {
  entityAliasFromSchemaKey,
  type FlattenedField,
} from '@/utils/contractGeneration/templateSchemaFields';
import { extractErrorDetail } from '@/utils/errorHandler';
import FieldPalette from '@/components/contractGeneration/templateAuthoring/FieldPalette.vue';
import AnnotationConfigPopover from '@/components/contractGeneration/templateAuthoring/AnnotationConfigPopover.vue';
import ControlFlowConfigDrawer from '@/components/contractGeneration/templateAuthoring/ControlFlowConfigDrawer.vue';
import TemplateValidationResults from './TemplateValidationResults.vue';
import TemplateSaveModal from './TemplateSaveModal.vue';
import TemplateSamplePreviewModal from './TemplateSamplePreviewModal.vue';
import { useGaiaToast } from '@/composables/useGaiaToast';

/**
 * "Editor" tab — everything about authoring a template in the CURRENT Word
 * document: test it for validity, insert fields (configure-first, same
 * drawer as the web app), and save it back to GAIA as a template / variant /
 * version. Browsing existing templates lives on the My templates tab.
 */

const store = useOfficeTemplateStore();
const message = useGaiaToast();

// ── Test current document + save ────────────────────────────────────────

const saveModalVisible = ref<boolean>(false);

function handleValidate(): void {
  void store.validateLiveDocument();
}

// ── Sample-data preview ─────────────────────────────────────────────────
// Exports the live document and lets the backend fill every field with a
// generated sample value (template defaults win), then shows the rendered
// result so the author can judge the filled-in layout.

const previewingSample = ref<boolean>(false);
const samplePreviewBlob = ref<Blob | null>(null);
const samplePreviewVisible = ref<boolean>(false);

async function handleSamplePreview(): Promise<void> {
  if (previewingSample.value) return;
  previewingSample.value = true;
  try {
    const docx = await exportCurrentDocumentAsDocx('document.docx');
    samplePreviewBlob.value = await TemplateAuthoringService.previewSampleUpload(docx);
    samplePreviewVisible.value = true;
  } catch (error) {
    message.error(extractErrorDetail(error, 'Failed to render the sample preview.'));
  } finally {
    previewingSample.value = false;
  }
}

// ── Field palette (click → configure → insert at cursor) ───────────────

const inserting = ref<boolean>(false);

/**
 * Turn a rendered `{{ expr }}` tag into a non-printing `{% set _ = expr %}`.
 * Used when the config drawer's "Don't show in document" switch is on, so the
 * value is collected as input but never rendered (e.g. `age` driving an
 * `{% if age < 18 %}` condition).
 */
function toHiddenSet(jinja: string): string {
  // Capture the inner expression of a `{{ … }}` tag, trimmed of surrounding
  // whitespace — e.g. `{{ age | format_number("en") }}` → `age | format_number("en")`.
  const match = jinja.match(/^\{\{\s*([\s\S]*?)\s*\}\}$/);
  const expr = match ? match[1] : jinja;
  return `{% set _ = ${expr} %}`;
}

/**
 * Entity/object aliases minted while inserting — feeds the drawer's role
 * dropdown so repeated inserts can reuse the same role (e.g. `seller`).
 * Session-only, like the snippet library.
 */
const sessionInstances = ref<AnnotationInstance[]>([]);

/**
 * Roles/groups already present in the DOCUMENT, derived from the last "Test
 * current document" run: top-level schema entries typed legal_entity /
 * natural_person are entity roles, object-typed entries are groups. Re-runs
 * of the test refresh this automatically.
 */
const detectedInstances = computed<AnnotationInstance[]>(() => {
  const fields = store.validationResult?.fields ?? {};
  const instances: AnnotationInstance[] = [];
  for (const [key, field] of Object.entries(fields)) {
    if (
      field.type === TemplateFieldType.LEGAL_ENTITY ||
      field.type === TemplateFieldType.NATURAL_PERSON
    ) {
      instances.push({
        kind: 'entity',
        entity_type: field.type,
        // Schema keys carry the entity-type prefix (`natural_person_friend`);
        // the role/alias is what's left after it (`friend`) — the renderer
        // re-adds the prefix when materializing tags.
        alias: entityAliasFromSchemaKey(key, field),
        used_sub_fields: Object.keys(field.fields ?? {}),
      });
    } else if (field.type === TemplateFieldType.OBJECT) {
      instances.push({
        kind: 'object',
        entity_type: null,
        alias: key,
        used_sub_fields: Object.keys(field.fields ?? {}),
      });
    }
  }
  return instances;
});

/** Detected (document) roles first, then session-minted ones not yet in it. */
const availableInstances = computed<AnnotationInstance[]>(() => {
  const merged = [...detectedInstances.value];
  for (const instance of sessionInstances.value) {
    const exists = merged.some(
      existing =>
        existing.kind === instance.kind &&
        existing.alias === instance.alias &&
        (existing.kind !== 'entity' || existing.entity_type === instance.entity_type)
    );
    if (!exists) merged.push(instance);
  }
  return merged;
});

// Field drawer state ----------------------------------------------------
// The one drawer serves two flows: configuring a palette pick to insert at
// the cursor, and editing a detected field in place. The mode is stamped
// when the drawer opens (not reset on close), so the apply/remove events —
// which the drawer emits just before closing itself — always see the mode
// they were opened with.
const drawerVisible = ref<boolean>(false);
const drawerDraft = ref<AnnotationDraft | null>(null);
const drawerFieldType = ref<TemplateFieldType | null>(null);
const drawerMode = ref<'insert' | 'edit'>('insert');
/** Schema path of the detected field being edited (edit mode only). */
const editFieldPath = ref<string | null>(null);
const applyingEdit = ref<boolean>(false);

// Control-flow drawer state ---------------------------------------------
const cfDrawerVisible = ref<boolean>(false);
const cfDraft = ref<ControlFlowSnippet | null>(null);

onMounted(async () => {
  try {
    await store.loadFieldCatalog();
  } catch (error) {
    message.error(extractErrorDetail(error, 'Failed to load the field catalog.'));
  }
});

// --- Draft builders (mirror the snippet library) -----------------------

function makeLocalId(): string {
  return `office-snippet-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`;
}

/** The drawer requires an anchor on the draft but never reads it here. */
function stubAnchor(): AnnotationDraft['anchor'] {
  return {
    scope: { kind: 'body' },
    paragraph_index: 0,
    run_index: 0,
    offset_in_run: 0,
    snippet_before: '',
    snippet_after: '',
  };
}

function defaultFieldNameForPrimitive(type: TemplateFieldType): string {
  switch (type) {
    case TemplateFieldType.STRING:
      return 'text_field';
    case TemplateFieldType.NUMBER:
      return 'number_field';
    case TemplateFieldType.DATE:
      return 'date_field';
    case TemplateFieldType.SELECTION:
      return 'selection_field';
    case TemplateFieldType.BOOLEAN:
      return 'flag';
    default:
      return 'field';
  }
}

function defaultFiltersForPrimitive(type: TemplateFieldType): AnnotationFilter[] {
  if (type === TemplateFieldType.DATE) return [{ name: 'format_date', args: ['en', 'long'] }];
  if (type === TemplateFieldType.NUMBER) return [{ name: 'format_number', args: ['en'] }];
  return [];
}

function defaultAliasForEntity(entityType: 'legal_entity' | 'natural_person'): string {
  const existing = availableInstances.value.find(
    inst => inst.kind === 'entity' && inst.entity_type === entityType
  );
  if (existing) return existing.alias;
  return entityType === 'legal_entity' ? 'company' : 'person';
}

function defaultAliasForObject(): string {
  const existing = availableInstances.value.find(inst => inst.kind === 'object');
  return existing?.alias ?? 'data';
}

function captureAlias(draft: AnnotationDraft): void {
  const binding = draft.binding;
  if (!binding) return;
  if (binding.kind !== 'entity' && binding.kind !== 'object') return;

  const alias = binding.instance_alias.trim();
  if (!alias) return;

  const exists = sessionInstances.value.some(inst => {
    if (inst.kind !== binding.kind) return false;
    if (binding.kind === 'entity' && inst.kind === 'entity') {
      return inst.entity_type === binding.entity_type && inst.alias === alias;
    }
    return inst.alias === alias;
  });
  if (exists) return;

  const subField = binding.sub_field;
  const newInstance: AnnotationInstance =
    binding.kind === 'entity'
      ? {
          kind: 'entity',
          entity_type: binding.entity_type,
          alias,
          used_sub_fields: subField ? [subField] : [],
        }
      : {
          kind: 'object',
          entity_type: null,
          alias,
          used_sub_fields: subField ? [subField] : [],
        };
  sessionInstances.value = [...sessionInstances.value, newInstance];
}

// --- Palette handlers ---------------------------------------------------

function openDraft(
  draft: AnnotationDraft,
  fieldType: TemplateFieldType | null,
  mode: 'insert' | 'edit' = 'insert'
): void {
  drawerDraft.value = draft;
  drawerFieldType.value = fieldType;
  drawerMode.value = mode;
  drawerVisible.value = true;
}

function handlePickPrimitive(type: TemplateFieldType): void {
  openDraft(
    {
      local_id: makeLocalId(),
      kind: 'inline',
      anchor: stubAnchor(),
      field_name: defaultFieldNameForPrimitive(type),
      binding: { kind: 'primitive' },
      origin: 'user',
      filters: defaultFiltersForPrimitive(type),
      required: false,
    },
    type
  );
}

function handlePickEntityField(entity: FieldCatalogEntity, field: FieldCatalogField): void {
  const entityType = entity.name as 'legal_entity' | 'natural_person';
  openDraft(
    {
      local_id: makeLocalId(),
      kind: 'inline',
      anchor: stubAnchor(),
      field_name: null,
      binding: {
        kind: 'entity',
        entity_type: entityType,
        instance_alias: defaultAliasForEntity(entityType),
        sub_field: field.name,
      },
      origin: 'user',
      filters: [],
      required: false,
    },
    field.type
  );
}

function handlePickObjectField(): void {
  openDraft(
    {
      local_id: makeLocalId(),
      kind: 'inline',
      anchor: stubAnchor(),
      field_name: null,
      binding: {
        kind: 'object',
        instance_alias: defaultAliasForObject(),
        sub_field: 'field',
      },
      origin: 'user',
      filters: [],
      required: false,
    },
    null
  );
}

/**
 * Open the control-flow drawer. Defaults to inline (no %p) — the user opts
 * into whole-paragraph wrapping via the "Advanced options" toggle.
 */
function openControlFlow(snippet: ControlFlowSnippet): void {
  snippet.scope = 'inline';
  cfDraft.value = snippet;
  cfDrawerVisible.value = true;
}

function handlePickConditional(): void {
  openControlFlow(newConditionalSnippet());
}

function handlePickLoop(): void {
  openControlFlow(newLoopSnippet());
}

// --- Insertion -----------------------------------------------------------

async function insertSnippet(text: string): Promise<void> {
  if (!text) {
    message.error('The snippet could not be rendered.');
    return;
  }
  if (inserting.value) return;
  inserting.value = true;
  try {
    await insertTextAtSelection(text);
  } catch (error) {
    message.error(extractErrorDetail(error, 'Failed to insert the snippet into the document.'));
  } finally {
    inserting.value = false;
  }
}

function handleApply(patch: Partial<TemplateAnnotation>, options?: { hidden?: boolean }): void {
  if (!drawerDraft.value) return;
  if (drawerMode.value === 'edit') {
    void applyDetectedFieldEdit(patch, options);
    return;
  }
  const merged: AnnotationDraft = {
    ...drawerDraft.value,
    ...patch,
  };
  captureAlias(merged);
  const rendered = renderJinjaPreview(merged);
  void insertSnippet(options?.hidden ? toHiddenSet(rendered) : rendered);
}

// --- Detected-field editing (pencil on the detected fields list) ---------

/**
 * Open the config drawer on a detected field, prefilled from the last "Test
 * current document" schema. The field's inline tags are looked up in the
 * body text first — a field that only appears in `{% if %}`/`{% for %}`
 * expressions (or whose tag was edited away since the test) has nothing an
 * inline edit could rewrite, so the drawer doesn't open.
 */
async function handleEditDetectedField(field: FlattenedField): Promise<void> {
  const fields = store.validationResult?.fields ?? null;
  const leaf = fields ? resolveFieldAtPath(fields, field.path) : null;
  if (!fields || !leaf) {
    message.warning('Run "Test current document" again — the field list is out of date.');
    return;
  }
  try {
    const bodyText = await readDocumentBodyText();
    const extraction = extractFieldTagOccurrences(bodyText, field.path);
    if (extraction.count === 0) {
      message.warning(
        `Couldn't find "${field.displayKey}" as an editable {{ … }} tag — it may only appear in conditions or loops, or the document changed. Try "Test current document" again.`
      );
      return;
    }
  } catch (error) {
    message.error(extractErrorDetail(error, 'Failed to read the document.'));
    return;
  }
  editFieldPath.value = field.path;
  openDraft(
    buildDraftForDetectedField(field.path, leaf, fields, makeLocalId(), stubAnchor()),
    field.type === 'unknown' ? null : field.type,
    'edit'
  );
}

/**
 * Rewrite every occurrence of the edited field's tag in the document —
 * pass an empty replacement to remove the field. Occurrences are
 * re-extracted at apply time (the user may have edited the document while
 * the drawer was open), then the detected-fields list is refreshed by
 * re-running the validation.
 */
async function applyDetectedFieldEdit(
  patch: Partial<TemplateAnnotation>,
  options?: { hidden?: boolean },
  removal = false
): Promise<void> {
  if (!drawerDraft.value || !editFieldPath.value || applyingEdit.value) return;
  applyingEdit.value = true;
  try {
    const merged: AnnotationDraft = {
      ...drawerDraft.value,
      ...patch,
    };
    let replacement = '';
    if (!removal) {
      captureAlias(merged);
      const rendered = renderJinjaPreview(merged);
      replacement = options?.hidden ? toHiddenSet(rendered) : rendered;
    }
    const bodyText = await readDocumentBodyText();
    const extraction = extractFieldTagOccurrences(bodyText, editFieldPath.value);
    if (extraction.count === 0) {
      message.warning(
        'Couldn\'t find the field in the document anymore — run "Test current document" and try again.'
      );
      return;
    }
    const { replaced, missing } = await replaceAllTagOccurrences(extraction.finds, replacement);
    if (replaced > 0) {
      message.success(
        removal
          ? `Removed ${replaced} occurrence${replaced === 1 ? '' : 's'} from the document — surrounding text was left unchanged.`
          : `Updated ${replaced} occurrence${replaced === 1 ? '' : 's'} in the document.`
      );
    }
    if (missing.length > 0) {
      message.warning('Some occurrences could not be updated — re-test the document.');
    }
    // An edit only rewrites {{ … }} tags; after a rename, conditions and
    // loops keep pointing at the old variable.
    const oldRoot = editFieldPath.value.split('.')[0];
    if (
      !removal &&
      draftVariableRoot(merged) !== oldRoot &&
      blockTagsReferenceRoot(bodyText, oldRoot)
    ) {
      message.warning(`Conditions or loops still reference "${oldRoot}" — update them separately.`);
    }
    await store.validateLiveDocument();
  } catch (error) {
    message.error(extractErrorDetail(error, 'Failed to update the field in the document.'));
  } finally {
    applyingEdit.value = false;
  }
}

/** The drawer's Remove: in edit mode, delete every occurrence; inserts have nothing to remove. */
function handleRemove(): void {
  if (drawerMode.value !== 'edit') return;
  void applyDetectedFieldEdit({}, undefined, true);
}

/**
 * Wrap the document's selected block in the condition/loop when one exists
 * (decided by the ACTUAL selection at apply time, not the tracked flag —
 * the user may have clicked elsewhere while the drawer was open); otherwise
 * fall back to inserting the full empty block at the cursor.
 */
async function insertControlFlow(snippet: ControlFlowSnippet): Promise<void> {
  if (inserting.value) return;
  inserting.value = true;
  try {
    const { prefix, suffix } = renderControlFlowWrapParts(snippet);
    // Wrap the selection, or drop an empty block at the cursor. Inline keeps
    // the tags on the same line (a mid-sentence condition).
    const wrapped = await wrapSelectionWithBlock(prefix, suffix, {
      inline: snippet.scope === 'inline',
    });
    if (!wrapped) {
      await insertTextAtSelection(renderControlFlowSnippet(snippet));
    }
  } catch (error) {
    message.error(extractErrorDetail(error, 'Failed to insert the snippet into the document.'));
  } finally {
    inserting.value = false;
  }
}

function handleApplyControlFlow(snippet: ControlFlowSnippet): void {
  void insertControlFlow(snippet);
}
</script>

<template>
  <n-flex vertical :size="14" :wrap="false">
    <!-- The document's actions: check it, see it filled, keep it. -->
    <n-flex align="center" justify="end" :size="8">
      <gaia-tooltip placement="bottom-end" :style="{ maxWidth: '240px' }">
        <template #trigger>
          <gaia-button size="small" :loading="previewingSample" @click="handleSamplePreview">
            <template #icon>
              <n-icon :component="IconEye" />
            </template>
            Preview
          </gaia-button>
        </template>
        Fills the document's fields with sample values and shows the rendered result — template
        defaults are used where declared.
      </gaia-tooltip>
      <gaia-tooltip placement="bottom-end" :style="{ maxWidth: '240px' }">
        <template #trigger>
          <gaia-button
            size="small"
            :loading="store.validationStatus === 'running'"
            @click="handleValidate"
          >
            <template #icon>
              <n-icon :component="IconCheckCircle" />
            </template>
            Test document
          </gaia-button>
        </template>
        Checks whether this Word document (including unsaved edits) is a valid template, and lists
        any errors.
      </gaia-tooltip>
      <gaia-tooltip placement="bottom-end" :style="{ maxWidth: '240px' }">
        <template #trigger>
          <gaia-button
            type="primary"
            size="small"
            :disabled="store.savingDocument"
            @click="saveModalVisible = true"
          >
            <template #icon>
              <n-icon :component="IconAdd" />
            </template>
            Save as template
          </gaia-button>
        </template>
        Saves the document you're working on in Word as a new template, a new variant, or a new
        version of an existing one.
      </gaia-tooltip>
    </n-flex>

    <template-validation-results
      :status="store.validationStatus"
      :result="store.validationResult"
      :error-message="store.validationError"
      @edit-field="handleEditDetectedField"
    />

    <gaia-divider variant="subtle" :style="{ margin: 0 }" />

    <n-spin :show="inserting">
      <field-palette
        :catalog="store.fieldCatalog"
        :loading="store.fieldCatalogLoading"
        :draggable="false"
        show-control-flow
        hint="Click a tile to configure the field, then insert it at your cursor in the document. Selecting text first and applying a Condition or Loop wraps it."
        class="office-palette"
        @pick-primitive="handlePickPrimitive"
        @pick-entity-field="handlePickEntityField"
        @pick-object-field="handlePickObjectField"
        @pick-conditional="handlePickConditional"
        @pick-loop="handlePickLoop"
      >
        <template #footer>
          For entity sub-fields, pick a role (e.g. <code>seller</code>, <code>buyer</code>) in the
          configuration drawer — reuse the same role to refer to the same person.
        </template>
      </field-palette>
    </n-spin>

    <annotation-config-popover
      v-model:show="drawerVisible"
      :annotation="drawerDraft"
      :reference="null"
      :filter-catalog="store.fieldCatalog?.filters ?? []"
      :entity-catalog="store.fieldCatalog?.entities ?? []"
      :field-type="drawerFieldType"
      :instances="availableInstances"
      :preview-only="false"
      show-hidden
      @apply="handleApply"
      @remove="handleRemove"
    />

    <control-flow-config-drawer
      v-model:show="cfDrawerVisible"
      :snippet="cfDraft"
      :editing="false"
      @apply="handleApplyControlFlow"
    />

    <template-save-modal v-model:show="saveModalVisible" />
    <template-sample-preview-modal v-model:show="samplePreviewVisible" :blob="samplePreviewBlob" />
  </n-flex>
</template>

<style scoped>
/* The palette was built as a full-height left sidebar; inside the task pane
   tab it's a plain block, so drop its sidebar chrome. */
.office-palette {
  border-right: none;
  padding: 8px 0;
  height: auto;
}
</style>
